/**
 * Attached Files List Component
 *
 * Displays confirmed file attachments that are ready to be sent with the message.
 * Shows file name, size, and allows removal. Image attachments show a thumbnail
 * and a larger hover preview.
 */

import { FileText, X } from 'lucide-react';
import React from 'react';
import type { DraftAttachment } from '@ctrl-enter/types';
import { cn } from '../utils/cn';

export type AttachedFilesListProps = {
  attachments: DraftAttachment[];
  onRemove: (index: number) => void;
  className?: string;
};

const isImage = (attachment: DraftAttachment): boolean => {
  return (
    typeof attachment.contentType === 'string' && attachment.contentType.startsWith('image/')
  );
};

type AttachmentChipProps = {
  attachment: DraftAttachment;
  onRemove: () => void;
};

const AttachmentChip: React.FC<AttachmentChipProps> = ({ attachment, onRemove }) => {
  const [showPreview, setShowPreview] = React.useState(false);
  const showImage = isImage(attachment) && !!attachment.url;

  return (
    <div
      className="relative flex items-center gap-2 bg-muted/50 rounded-lg px-3 py-2 text-sm"
      onMouseEnter={() => showImage && setShowPreview(true)}
      onMouseLeave={() => setShowPreview(false)}
      onFocus={() => showImage && setShowPreview(true)}
      onBlur={() => setShowPreview(false)}
    >
      {showImage ? (
        <img
          src={attachment.url}
          alt={attachment.name}
          className="w-6 h-6 rounded object-cover border border-border/40 shrink-0"
        />
      ) : (
        <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
      )}
      <span className="text-foreground truncate max-w-[200px]" title={attachment.name}>
        {attachment.name}
      </span>
      {attachment.fileSizeMB !== undefined && (
        <span className="text-xs text-muted-foreground shrink-0">
          ({attachment.fileSizeMB.toFixed(2)} MB)
        </span>
      )}
      <button
        type="button"
        onClick={onRemove}
        className="ml-1 text-muted-foreground hover:text-destructive transition-colors"
        aria-label={`Remove ${attachment.name}`}
      >
        <X className="w-4 h-4" />
      </button>

      {/* Hover preview — only for images */}
      {showImage && showPreview && (
        <div
          className={cn(
            'absolute bottom-full left-0 mb-2 z-50',
            'pointer-events-none',
            'animate-in fade-in zoom-in-95 duration-150'
          )}
          role="tooltip"
        >
          <div
            className={cn(
              'rounded-lg overflow-hidden',
              'border border-border/60 bg-background',
              'shadow-xl'
            )}
          >
            <img
              src={attachment.url}
              alt={attachment.name}
              className="block max-w-[280px] max-h-[200px] object-contain bg-muted/20"
            />
            <div className="px-2.5 py-1.5 text-xs text-muted-foreground border-t border-border/40 bg-background/95">
              <div className="truncate text-foreground font-medium">{attachment.name}</div>
              {attachment.fileSizeMB !== undefined && (
                <div className="text-[11px] text-muted-foreground/70">
                  {attachment.fileSizeMB.toFixed(2)} MB
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const AttachedFilesList: React.FC<AttachedFilesListProps> = ({
  attachments,
  onRemove,
  className,
}) => {
  if (attachments.length === 0) return null;

  return (
    <div className={cn('mb-3', className)}>
      <div className="glass-surface rounded-xl p-3 border border-border">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-muted-foreground">
            Attached Files ({attachments.length})
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {attachments.map((attachment, index) => (
            <AttachmentChip
              key={attachment.id || `${attachment.name}-${index}`}
              attachment={attachment}
              onRemove={() => onRemove(index)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

AttachedFilesList.displayName = 'AttachedFilesList';
